// src/components/Dashboard/DashboardAdmin.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { logout } from "../../services/authService";
import { usePermisos } from "../../context/PermisosContext";
import { useAuth } from "../../context/AuthContext";
import SelectorCuenta from "./SelectorCuenta";
import { prefetchNavigationData } from "../../services/Api";
import { isSuperAdmin } from "../../services/authUtils";

// Vistas
import Inicial from "../Inicio/Inicial";
import UsuariosTemporales from "../UsuariosTemporales/UsuariosTemporales";
import Estanques from "../Estanques/Estanques";
import Dispositivos from "../Dispositivos/Dispositivos";
import Sensores from "../Sensores/Sensores";
import Efectores from "../Efectores/Efectores";
import Automatizacion from "../Automatizacion/Automatizacion";
import Alarmas from "../Alarmas/Alarmas";
import Reporteria from "../Reporteria/Reporteria";
import Configuraciones from "../Configuraciones/Configuraciones";
import AcercaDe from "../AcercaDe/AcercaDe";
import Contacto from "../Contacto/Contacto";

const MENUS = [
  "Inicio",
  "Estanques",
  "Dispositivos",
  "Sensores",
  "Efectores",
  "Automatización",
  "Alarmas",
  "Usuarios Temporales",
  "Reportería",
  "Configuraciones",
  "Acerca de",
  "Contáctanos",
];

const MENU_FEATURE = {
  Inicio: "INICIO_VIEW",
  Estanques: "ESTANQUES_VIEW",
  Dispositivos: "DISPOSITIVOS_VIEW",
  Sensores: "SENSORES_VIEW",
  Efectores: "EFECTORES_VIEW",
  "Usuarios Temporales": "USUARIOS_TEMP_VIEW",
  Automatización: "AUTOMATIZACION_VIEW",
  Alarmas: "ALARMAS_VIEW",
  Reportería: "REPORTERIA_VIEW",
  Configuraciones: "CONFIGURACIONES_VIEW",
  "Acerca de": "ACERCA_VIEW",
  "Contáctanos": "CONTACTO_VIEW",
};

// Las subpestañas pertenecen a un módulo concreto. Centralizar esta regla evita
// URLs imposibles como `tab=Sensores&sub=mis-estadisticas`, que antes podían
// provocar que dos componentes corrigieran la URL en sentidos opuestos.
const SUBS_CONOCIDOS = {
  Estanques: ["tablero", "crear", "administrar"],
  Dispositivos: ["tablero", "crear", "administrar", "asistente"],
  "Automatización": ["tablero", "sensores", "efectores"],
  Efectores: ["tablero", "administrar", "crear"],
  Inicio: ["mis-estadisticas", "mis-sensores", "resumen", "bienvenida"],
  Sensores: ["tablero", "crear", "administrar"],
  Alarmas: ["tablero", "programar", "administrar", "historial"],
};

const SUB_PREDETERMINADO = {
  Estanques: "tablero",
  Dispositivos: "tablero",
  "Automatización": "tablero",
  Efectores: "tablero",
  Inicio: "bienvenida",
  Sensores: "tablero",
  Alarmas: "tablero",
};

const normalizarSub = (menu, sub) => {
  const conocidos = SUBS_CONOCIDOS[menu];
  if (!conocidos) return sub || "";
  return conocidos.includes(sub) ? sub : SUB_PREDETERMINADO[menu];
};

const DashboardAdmin = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const email = localStorage.getItem("email") || "";
  const rol = localStorage.getItem("rol") || "";
  const { can, loading } = usePermisos();

  useEffect(() => {
    const timer = window.setTimeout(() => prefetchNavigationData(), 250);
    return () => window.clearTimeout(timer);
  }, []);

  // ===== Estado inicial desde URL =====
  const [menuActivo, setMenuActivo] = useState(() => {
    const tab = new URLSearchParams(window.location.search).get("tab");
    return tab && MENUS.includes(tab) ? tab : "Inicio";
  });

  // Recordamos el último `sub` por menú (para TODOS los módulos con tabs)
  const [subByMenu, setSubByMenu] = useState(() => {
    const qs = new URLSearchParams(window.location.search);
    const tab = qs.get("tab") || "Inicio";
    const sub = normalizarSub(tab, qs.get("sub") || "");
    return { [tab]: sub };
  });

  // estados UI
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768);

  // Menús visibles (por permisos)
  const menusVisibles = useMemo(() => {
    if (loading) return [];
    return MENUS.filter((m) => {
      const feature = MENU_FEATURE[m];
      return feature ? can(feature) : true;
    });
  }, [loading, can]);

  // ===== Escucha cambios externos en la URL (back/forward o deep links) =====
  useEffect(() => {
    const tab = searchParams.get("tab");
    const sub = searchParams.get("sub") || "";

    if (tab && MENUS.includes(tab)) {
      const subNormalizado = normalizarSub(tab, sub);
      // actualiza menú si cambió
      setMenuActivo((prev) => (prev !== tab ? tab : prev));

      // recuerda el sub para ese menú (no importa cuál sea el módulo)
      setSubByMenu((prev) =>
        prev[tab] === subNormalizado ? prev : { ...prev, [tab]: subNormalizado }
      );

      // Corrige enlaces antiguos o contaminados antes de montar el módulo.
      if (subNormalizado !== sub) {
        const next = new URLSearchParams(searchParams);
        if (subNormalizado) next.set("sub", subNormalizado);
        else next.delete("sub");
        setSearchParams(next, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // ===== Espejo estado -> URL =====
  useEffect(() => {
    // Cuando llega un enlace interno hacia otro menú, primero dejamos que el
    // efecto anterior incorpore tab/sub al estado. Así no restauramos por
    // accidente la última subpestaña visitada (por ejemplo, Perfil).
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl && MENUS.includes(tabFromUrl) && tabFromUrl !== menuActivo) return;

    const next = new URLSearchParams(searchParams);

    // escribe tab
    if (!next.get("tab") || next.get("tab") !== menuActivo) {
      next.set("tab", menuActivo);
    }

    // escribe sub guardado para el menú activo (si existe)
    const currentSub = subByMenu[menuActivo];
    if (currentSub != null && currentSub !== next.get("sub")) {
      // si sub vacío, quitamos; si tiene valor, lo seteamos
      if (String(currentSub).trim() === "") {
        next.delete("sub");
      } else {
        next.set("sub", currentSub);
      }
    }

    // evitar loops
    const current = searchParams.toString();
    const target = next.toString();
    if (current !== target) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuActivo, subByMenu]);

  // Si el menú activo deja de estar permitido, cae al primero permitido
  useEffect(() => {
    if (loading) return;
    if (!menusVisibles.length) return;
    if (!menusVisibles.includes(menuActivo)) {
      setMenuActivo(menusVisibles[0]);
    }
  }, [loading, menusVisibles, menuActivo]);

  // ESC cierra sidebar en móvil
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setSidebarOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Responsivo
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 768px)");
    const handleChange = (e) => {
      setIsDesktop(e.matches);
      setSidebarOpen(e.matches);
    };
    handleChange(mql);
    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, []);

  // ===== helpers para sub (los hijos pueden llamarlos) =====
  const setSubForMenu = useCallback((menu, sub) => {
    const nextSub = normalizarSub(menu, sub || "");
    setSubByMenu((prev) =>
      prev[menu] === nextSub ? prev : { ...prev, [menu]: nextSub }
    );
  }, []);
  const setSubActual = useCallback((sub) => setSubForMenu(menuActivo, sub), [menuActivo, setSubForMenu]);

  // La URL es la fuente de verdad. No montamos el componente de destino antes
  // de actualizarla porque sus efectos de subpestañas podrían escribir todavía
  // sobre la URL anterior y abrir un módulo distinto.
  const cambiarMenu = (menu) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", menu);
    const rememberedSub = ["Estanques", "Dispositivos", "Sensores", "Efectores", "Alarmas", "Automatización"].includes(menu) ? "tablero" : normalizarSub(menu, subByMenu[menu]);
    if (rememberedSub) next.set("sub", rememberedSub);
    else next.delete("sub");
    setSearchParams(next);
    if (!isDesktop) setSidebarOpen(false);
  };

  const { setUser } = useAuth();
  const handleLogout = async () => {
    try {
      await logout?.();
    } catch (e) {
      console.warn("logout() falló (se sigue limpiando):", e);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("email");
      localStorage.removeItem("rol");
      localStorage.removeItem("endpoint");
      setUser(null);
      navigate("/", { replace: true });
    }
  };

  // ===== Contenido central =====
  const renderContenido = () => {
    if (loading) {
      return (
        <div className="bg-neutral-900 rounded-lg shadow-lg p-6">
          <p className="text-gray-300">Cargando permisos...</p>
        </div>
      );
    }

    const fView = MENU_FEATURE[menuActivo];
    if (fView && !can(fView)) {
      return (
        <div className="bg-neutral-900 rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-semibold text-red-400 mb-2">Acceso restringido</h3>
          <p className="text-gray-300">
            No cuentas con permisos para ver <span className="font-semibold">{menuActivo}</span>.
          </p>
        </div>
      );
    }

    // InitialSub es lo que tengamos guardado para ese módulo
    const initialSub = subByMenu[menuActivo];

    switch (menuActivo) {
      case "Inicio":
        // Inicio ya gestiona su propio ?sub (lo leerá de la URL).
        return (
          <div className="animate-slide-in">
            <Inicial />
          </div>
        );

      case "Estanques":
        return (
          <div className="animate-slide-in">
            <Estanques
              initialTab={initialSub || "tablero"}
              onTabChange={(t) => setSubForMenu("Estanques", t)}
            />
          </div>
        );

      case "Dispositivos":
        return (
          <div className="animate-slide-in">
            <Dispositivos
              initialSub={initialSub}           // si tu componente lo usa
              onSubChange={setSubActual}        // si tu componente lo emite
            />
          </div>
        );

      case "Sensores":
        return (
          <div className="animate-slide-in">
            <Sensores
              initialTab={initialSub || "tablero"}
              onTabChange={(t) => setSubForMenu("Sensores", t)}
            />
          </div>
        );

      case "Efectores":
        return (
          <div className="animate-slide-in">
            <Efectores
              initialSub={initialSub}
              onSubChange={setSubActual}
            />
          </div>
        );

      case "Automatización":
        return (
          <div className="animate-slide-in">
            <Automatizacion
              initialSub={initialSub}
              onSubChange={setSubActual}
            />
          </div>
        );

      case "Alarmas":
        return (
          <div className="animate-slide-in">
            <Alarmas initialSub={initialSub || "tablero"} onSubChange={setSubActual} />
          </div>
        );

      case "Usuarios Temporales":
        return (
          <div className="animate-slide-in">
            <UsuariosTemporales
              initialSub={initialSub}
              onSubChange={setSubActual}
            />
          </div>
        );

      case "Reportería":
        return (
          <div className="animate-slide-in">
            <Reporteria
              initialSub={initialSub}
              onSubChange={setSubActual}
            />
          </div>
        );

      case "Configuraciones":
        return (
          <Configuraciones
            initialTab={initialSub || "perfil"}
            onSubChange={setSubActual}
          />
        );

      case "Acerca de":
        return <AcercaDe />;

      case "Contáctanos":
        return <Contacto />;

      default:
        return (
          <div className="bg-neutral-900 rounded-lg shadow-lg p-6">
            <p className="text-gray-300">Selecciona una opción del menú.</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-dvh w-full bg-transparent">
      {/* Overlay móvil */}
      {sidebarOpen && !isDesktop && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-[1px] z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 h-dvh w-64 overflow-y-auto bg-slate-900 text-white z-40 transition-all duration-300 ${
          sidebarOpen ? "left-0" : "-left-64"
        }`}
      >
        <div className="py-6 px-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold">MENÚ</h3>
            <button
              className="p-2 rounded hover:bg-slate-800"
              onClick={() => setSidebarOpen((s) => !s)}
              aria-label="Abrir/Cerrar menú"
              title="Abrir/Cerrar menú"
            >
              ☰
            </button>
          </div>

          <nav className="mt-4 space-y-2">
            {loading ? (
              <div className="text-sm text-gray-400 px-2">Cargando...</div>
            ) : (
              menusVisibles.map((menu) => (
                <button
                  key={menu}
                  onClick={() => cambiarMenu(menu)}
                  className={`w-full text-left py-2 px-4 rounded transition-colors duration-200 hover:bg-cyan-700 ${
                    menuActivo === menu ? "bg-cyan-600" : ""
                  }`}
                >
                  {menu}
                </button>
              ))
            )}
          </nav>
        </div>
      </aside>

      {/* Contenido */}
      <main
        className={`dashboard-glass transition-[margin] duration-300 p-4 md:p-6 overflow-auto bg-transparent min-h-dvh ${
          sidebarOpen && isDesktop ? "md:ml-64" : "md:ml-0"
        }`}
      >
        <div className="dashboard-header">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => setSidebarOpen(true)}
              className={`${
                sidebarOpen ? "hidden" : "inline-flex"
              } h-12 w-12 shrink-0 items-center justify-center rounded hover:bg-slate-800 text-white border border-slate-700`}
              aria-label="Abrir menú"
              title="Abrir menú"
            >
              ☰
            </button>

            <div className="min-w-0 break-words">
              <h2 className="text-xl sm:text-2xl font-bold text-green-500">
                CONTROL DE ESTANQUES
              </h2>
              <p className="text-gray-300 text-sm mt-1">
                Bienvenido, <span className="font-semibold">{email}</span>
                {rol ? <span className="opacity-80"> — {rol}</span> : null}
              </p>
            </div>
          </div>

          <div className="dashboard-header-actions">
            <SelectorCuenta />
            {!loading && can("DISPOSITIVOS_VIEW") && can("DISPOSITIVOS_TAB_ASISTENTE_VIEW") && (
              <button
                onClick={() => {
                  setSearchParams({ tab: "Dispositivos", sub: "asistente" });
                  if (!isDesktop) setSidebarOpen(false);
                }}
                className="bg-gray-700 hover:bg-gray-600 text-white px-3 sm:px-4 py-2 rounded text-sm"
              >
                Asistente opcional
              </button>
            )}
            {menuActivo === "Inicio" && isSuperAdmin() ? (
              <a
                href="/configuracion-avanzada/"
                target="_blank"
                rel="noopener noreferrer"
                className="border border-cyan-600/60 bg-cyan-950/40 hover:bg-cyan-900/50 text-cyan-100 px-3 py-2 rounded text-sm"
              >
                Configuración avanzada
              </a>
            ) : null}
            <button
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700 text-white px-3 sm:px-4 py-2 rounded text-sm shadow"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>

        <div className="mt-4 md:mt-6">{renderContenido()}</div>
      </main>
    </div>
  );
};

export default DashboardAdmin;
